import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
    Button,
    TextField,
    MuiThemeProvider,
    createMuiTheme,
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel,
    Checkbox, Typography
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useHistory, useParams } from "react-router-dom";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getSubPlanById, postSubPlan, updateSubPlan, deleteSubPlan } from "../../../../api/subPlan";
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock';
import TablePlan from './TablePlan';

// Create theme for delete button (red)
const theme = createMuiTheme({
    palette: {
        secondary: {
            main: "#F64E60",
        },
    },
});

function getTargets() {
    return ["Seller", "Buyer", "Advisor"]
}

function getEmptyFeatures() {
    return {
        title: {
            'es': "",
            'en': ""
        },
        order: -1
    }
}

function getEmptySubPlan() {
    return {
        title: {
            'es': "",
            'en': ""
        },
        subTitle: {
            'es': "",
            'en': ""
        },
        monthPrice: null,
        yearPrice: null,
        target: "",
        features: [],
        mostPopular: false,
        active: true
    };
}

function featureDialog(featuresOpen, setFeaturesOpen, featureSelected, setFeatureSelected, featuresData, setFeaturesData, setTableKey) {

    const dialogTitle = featureSelected.order < 0 ? "Create feature" : "Edit feature"


    const handleChange = (element, lang) => (event) => {
        if (lang) {
            if (event.target.value === ' ')
                return
            if (!featureSelected[element])
                featureSelected[element] = {}
            let newText = featureSelected[element]
            newText[lang] = event.target.value
            setFeatureSelected({ ...featureSelected, [element]: newText })
        } else
            setFeatureSelected({ ...featureSelected, [element]: event.target.value })
    }

    const renderMultilanguageTabContent = lang => {
        return (
            <>
                <TextField
                    label="Title"
                    value={(featureSelected.title && featureSelected.title[lang]) || ''}
                    onChange={handleChange('title', lang)}
                    margin="normal"
                    variant="outlined"
                    multiline
                    minRows={1}
                    maxRows={3}
                    required
                />
            </>
        )
    }

    return (
        <Dialog
            open={featuresOpen}
            onClose={() => setFeaturesOpen(false)}
            aria-labelledby="confirm-dialog"
            PaperProps={{
                style: {
                    width: '40vw',
                    height: '30vh',
                    maxWidth: 'none'
                }
            }}>
            <DialogTitle id="confirm-dialog">{dialogTitle}</DialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <MultilanguageTabBlock
                    multilanguageTabContent={renderMultilanguageTabContent}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                        if (!featureSelected.title?.es || featureSelected.title?.es.trim() === '') {
                            alertError({ customMessage: "Spanish title is required." });
                            return;
                        }

                        if (featureSelected.title?.en === '' || featureSelected.title?.en === '') {
                            alertError({ customMessage: "English title is required." });
                            return;
                        }

                        if (dialogTitle.includes("Create")) {
                            const lastOrder = featuresData.reduce((max, item) => Math.max(max, item.order || 0), 0);
                            const newBusiness = {
                                ...featureSelected,
                                order: featuresData.length === 0 ? lastOrder : lastOrder + 1,
                            };


                            setFeaturesData([...featuresData, newBusiness]);

                        } else {
                            const updatedData = featuresData.map((item, index) =>
                                index === featureSelected.order - 1 ? featureSelected : item
                            );
                            setFeaturesData(updatedData);
                        }
                        setTableKey(k => k + 1);
                        setFeatureSelected(getEmptyFeatures())
                        setFeaturesOpen(false)
                    }}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                        setFeaturesOpen(false)
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function EditSubPlanPage() {
    const subPlanId = useParams().id;
    const history = useHistory();
    const {
        isLoading: isLoadingData,
        disableLoading: disableLoadingData,
        ContentSkeleton,
    } = useSkeleton();


    const [subPlan, setSubPlan] = useState(getEmptySubPlan());
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [targets] = useState(getTargets())

    const [featuresData, setFeaturesData] = useState([])
    const [featuresOpen, setFeaturesOpen] = useState(false);
    const [featureSelected, setFeatureSelected] = useState(getEmptyFeatures())
    const [tableKey, setTableKey] = useState(0);


    useEffect(() => {
        if (!subPlanId) {
            disableLoadingData();
            return;
        }

        getSubPlanById(subPlanId)
            .then((res) => {
                const data = res.data
                data.target = data.target.charAt(0).toUpperCase() + data.target.slice(1).toLowerCase();
                setSubPlan(data);
                setFeaturesData(data.features)
                disableLoadingData();
            })
            .catch((error) => {
                alertError({
                    error: error,
                    customMessage: "Could not get the subscription plan.",
                });
                history.push("/subscriptionPlans");
            });
    }, [subPlanId, disableLoadingData, history]);

    function saveSubPlan() {
        subPlan.features = featuresData.map(item => ({ title: item.title, order: item.order }));

        if (!subPlanId) {
            postSubPlan(subPlan)
                .then((res) => {
                    alertSuccess({
                        title: "Saved!",
                        customMessage: "Subscription plan created successfully.",
                    });
                    history.push("/subscriptionPlans");
                })
                .catch((error) => {
                    alertError({
                        error: error,
                        customMessage: "Could not save the subscription plan.",
                    });
                });
        } else {
            updateSubPlan(subPlanId, subPlan)
                .then((res) => {
                    alertSuccess({
                        title: "Saved!",
                        customMessage: "Changes saved successfully.",
                    });
                    history.push("/subscriptionPlans");
                })
                .catch((error) => {
                    alertError({
                        error: error,
                        customMessage: "Could not save the subscription plan.",
                    });
                });
        }
    }

    const handleChange = (element) => (event) => {
        setSubPlan({ ...subPlan, [element]: event.target.value });
    };

    const handleChangeLang = (element, lang) => (event) => {
        if (lang) {
            if (event.target.value === ' ')
                return
            if (!subPlan[element])
                subPlan[element] = {}
            let newText = subPlan[element]
            newText[lang] = event.target.value
            setSubPlan({ ...subPlan, [element]: newText })
        } else
            setSubPlan({ ...subPlan, [element]: event.target.value })
    }

    const renderMultilanguageTabContent = lang => {
        return (
            <>
                <TextField
                    label="Title"
                    value={(subPlan.title && subPlan.title[lang]) || ''}
                    onChange={handleChangeLang('title', lang)}
                    margin="normal"
                    variant="outlined"
                    required
                />
                <TextField
                    label="Subtitle"
                    value={(subPlan.subTitle && subPlan.subTitle[lang]) || ''}
                    onChange={handleChangeLang('subTitle', lang)}
                    margin="normal"
                    variant="outlined"
                    required
                />
            </>
        )
    }

    if (isLoadingData)
        return <ContentSkeleton />;

    return (
        <>
            <Card>
                <CardHeader title="Edit subscription plan"></CardHeader>
                <CardBody>
                    <div className="d-flex align-item-center">
                        <div className="col-12">
                            <MultilanguageTabBlock
                                multilanguageTabContent={renderMultilanguageTabContent}
                            />
                        </div>
                    </div>
                    <div className="d-flex align-item-center">
                        {/*<div className="col-4">
                            <TextField
                                label="Title"
                                value={subPlan.title}
                                onChange={handleChange("title")}
                                margin="normal"
                                variant="outlined"
                                required
                            />
                        </div>*/}
                        <div className="col-3">
                            <Autocomplete
                                id='Targets'
                                options={targets}
                                value={subPlan.target || null}
                                onChange={(_, newValue) => {
                                    setSubPlan({
                                        ...subPlan,
                                        target: newValue
                                    })
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='For whom?'
                                        margin="normal"
                                        variant="outlined"
                                    />
                                )}
                                required
                            />
                        </div>
                        <div className="col-2">
                            <TextField
                                label="Price per month"
                                type="number"
                                value={subPlan.monthPrice}
                                onChange={handleChange("monthPrice")}
                                margin="normal"
                                variant="outlined"
                                required
                            />
                        </div>
                        <div className="col-2">
                            <TextField
                                label="Price per year"
                                type="number"
                                value={subPlan.yearPrice}
                                onChange={handleChange("yearPrice")}
                                margin="normal"
                                variant="outlined"
                                required
                            />
                        </div>
                    </div>

                    <div className="d-flex align-item-center mt-5">
                        <div className="col-3">
                            <Typography variant="h6" color="text.secondary">
                                Characteristics
                            </Typography>
                        </div>
                        <div className="col-8" />
                        <div className="d-flex justify-content-end col-1">
                            <button
                                type="button"
                                className="btn btn-primary text-white"
                                onClick={() => {
                                    setFeatureSelected(getEmptyFeatures());
                                    setFeaturesOpen(true);
                                }}

                            >
                                Add new
                            </button>
                        </div>
                    </div>

                    <TablePlan
                        data={featuresData}
                        setData={setFeaturesData}
                        setFeatureSelected={setFeatureSelected}
                        setFeaturesOpen={setFeaturesOpen}
                        tableKey={tableKey}
                        setTableKey={setTableKey}
                    />


                    <br />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={subPlan.mostPopular}
                                onChange={() => setSubPlan({ ...subPlan, mostPopular: !subPlan.mostPopular })}
                                name="checkActive"
                            />
                        }
                        label="Most popular"
                    />
                </CardBody>
            </Card>


            <div style={{ display: "flex", flexDirection: "row" }}>
                <Button
                    onClick={() => history.push("/subscriptionPlans")}
                    variant="outlined"
                    style={{ marginRight: "20px" }}
                >
                    Back
                </Button>

                <Button
                    onClick={() => saveSubPlan()}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                >
                    Save subscription plan
                </Button>

                {featureDialog(featuresOpen, setFeaturesOpen, featureSelected, setFeatureSelected, featuresData, setFeaturesData, setTableKey)}

                {subPlanId && (
                    <>
                        <MuiThemeProvider theme={theme}>
                            <Button
                                onClick={() => setOpenConfirmDialog(true)}
                                variant="outlined"
                                color="secondary"
                                style={{ marginRight: "20px" }}
                            >
                                Delete subscription plan
                            </Button>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    marginLeft: "auto",
                                }}
                            ></div>
                        </MuiThemeProvider>

                        <ConfirmDialog
                            title={"Are you sure you want to delete this subscription plan?"}
                            open={openConfirmDialog}
                            setOpen={setOpenConfirmDialog}
                            onConfirm={() => {
                                deleteSubPlan(subPlanId)
                                    .then((res) => {
                                        alertSuccess({
                                            title: "Deleted!",
                                            customMessage: "Subscription plan deleted successfully.",
                                        });
                                        history.push("/subscriptionPlans");
                                    })
                                    .catch((error) => {
                                        alertError({
                                            error: error,
                                            customMessage: "Could not delete the subscription plan.",
                                        });
                                    });
                            }}
                        />
                    </>
                )}
            </div>
        </>
    );
}
